// Pending GitHub PR-comment triggers — a debounce queue surfaced on the runs
// page so operators can see what's about to dispatch ("address-comments" runs
// queued after reviewer activity) and cancel or short-circuit a row.
//
// Always rendered so operators can confirm the queue is empty/quiet at a
// glance (vs. wondering whether the panel just hadn't mounted). Polled at
// the same cadence as the runs list via useBssPoll.

import { useCallback, useState } from "react";
import api from "../../api";
import useBssPoll from "../../hooks/useBssPoll";
import Icon from "./icons";
import { apiErrorMessage, relativeTime } from "./shared";

const POLL_INTERVAL = 15000;

function countdown(dispatchesAtIso, now) {
  const ms = new Date(dispatchesAtIso).getTime() - now;
  if (ms <= 0) return "any moment";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `in ${m}m ${s}s` : `in ${s}s`;
}

export default function PendingCommentTriggers({ onChange }) {
  const fetchTriggers = useCallback(
    () => api.bssCommentTriggers().then((res) => res.data),
    []
  );
  const { data, error, refresh } = useBssPoll(fetchTriggers, {
    interval: POLL_INTERVAL,
    shouldContinue: () => true
  });
  const [busyId, setBusyId] = useState(null);

  const items = (data && data.items) || [];
  const now = Date.now();

  const cancel = async (id) => {
    setBusyId(id);
    try {
      await api.bssCancelCommentTrigger(id);
      refresh();
      onChange && onChange();
    } finally {
      setBusyId(null);
    }
  };

  const dispatchNow = async (id) => {
    setBusyId(id);
    try {
      await api.bssDispatchCommentTriggerNow(id);
      refresh();
      onChange && onChange();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="bss-card"
      style={{ marginBottom: 12, padding: "8px 12px" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
          fontSize: 13,
          fontWeight: 600,
          color: "var(--bss-info)"
        }}
      >
        <Icon.MessageSquare size={14} />
        Pending comment triggers ({items.length})
        <span
          style={{ fontWeight: 400, color: "var(--bss-text-dim)", marginLeft: 4 }}
        >
          — waiting for the reviewer to finish before dispatching
        </span>
      </div>

      {error && (
        <div style={{ color: "var(--bss-danger)", fontSize: 12 }}>
          {apiErrorMessage(error)}
        </div>
      )}

      {items.length === 0 ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--bss-text-dim)",
            padding: "4px 2px"
          }}
        >
          No pending triggers — drop a review comment on an agent PR and one
          will appear here after a few seconds.
        </div>
      ) : (
      <table className="bss-table" style={{ marginTop: 0 }}>
        <thead>
          <tr>
            <th style={{ width: 110 }}>Issue</th>
            <th style={{ width: 90 }}>PR</th>
            <th>Branch</th>
            <th style={{ width: 80, textAlign: "right" }}>Comments</th>
            <th style={{ width: 140 }}>Last comment</th>
            <th style={{ width: 140 }}>Dispatches</th>
            <th style={{ width: 200 }} aria-label="actions" />
          </tr>
        </thead>
        <tbody>
          {items.map((t) => {
            const busy = busyId === t.id;
            return (
              <tr key={t.id}>
                <td>{t.issueKey}</td>
                <td>#{t.prNumber}</td>
                <td
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 320
                  }}
                  title={t.branchName}
                >
                  {t.branchName}
                </td>
                <td style={{ textAlign: "right" }}>{t.commentCount}</td>
                <td>{relativeTime(t.lastCommentAt)}</td>
                <td>{countdown(t.dispatchesAt, now)}</td>
                <td style={{ textAlign: "right" }}>
                  <button
                    type="button"
                    className="bss-btn bss-btn-sm"
                    style={{ marginRight: 6 }}
                    disabled={busy}
                    onClick={() => dispatchNow(t.id)}
                    title="Skip the debounce wait and dispatch on the next tick"
                  >
                    Dispatch now
                  </button>
                  <button
                    type="button"
                    className="bss-btn bss-btn-sm bss-btn-danger"
                    disabled={busy}
                    onClick={() => cancel(t.id)}
                    title="Cancel — the row is removed; new comments will create a fresh trigger"
                  >
                    Cancel
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      )}
    </div>
  );
}
